import { useState, useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import PersonSharpIcon from '@mui/icons-material/PersonSharp';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { NavLinksType } from '../../../scripts/customPropTypes';
import Link from '@mui/material/Link';
import { Link as ReactLink, useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import { clearChatAndReset } from '@ashn/chatbot-react/Chatbot';
import { clearAmplitudeUser } from '../../../utils/analytics';

const UserProfileDropDown = ({ profileRoutes }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const { pathname } = useLocation();
	const triggerRef = useRef(null);
	const paperRef = useRef(null);

	const handleOpen = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	// Boost renders outside React at a high z-index, bypassing MUI's backdrop.
	// Capture-phase listener closes the menu on any in-page click outside it;
	// window blur closes it when focus shifts into the chat iframe.
	useEffect(() => {
		const onDocClick = e => {
			if (!open) return;
			if (triggerRef.current?.contains(e.target)) return;
			if (paperRef.current?.contains(e.target)) return;
			handleClose();
		};
		const onBlur = () => { if (open) handleClose(); };
		document.addEventListener('click', onDocClick, true);
		window.addEventListener('blur', onBlur);
		return () => {
			document.removeEventListener('click', onDocClick, true);
			window.removeEventListener('blur', onBlur);
		};
	}, [open]);

	const handleLogoutClick = () => {
		clearAmplitudeUser();
		clearChatAndReset();
		window.location.href = '/auth/logout'; // Manually handle the redirect it's not a react route
	};

	const selectMenuItem = link => {
		if (link.isReactPage) {
			return (
				<MenuItem
					component={ReactLink}
					to={link.href}
					reloadDocument={pathname === link.href}
					key={link.href}
					onClick={handleClose}
				>
					{link.label}
				</MenuItem>
			);
		}

		if (link.href === '/auth/logout') {
			return (
				<MenuItem
					component={Button}
					key={link.href}
					data-testid={link.label}
					onClick={handleLogoutClick}
					sx={{ textTransform: 'none' }}
				>
					{link.label}
				</MenuItem>
			);
		}

		return (
			<MenuItem
				component={Link}
				href={link.href}
				key={link.href}
				data-testid={link.label}
			>
				{link.label}
			</MenuItem>
		);
	};

	return (
		<>
			<IconButton
				ref={triggerRef}
				aria-controls={open ? 'basic-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				onClick={handleOpen}
				color="inherit"
				aria-label="user profile drop down"
				data-testid="user-profile"
			>
				<PersonSharpIcon />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				PaperProps={{ ref: paperRef }}
				slotProps={{ backdrop: { sx: { pointerEvents: 'none' } } }}
				disableScrollLock
				sx={{ zIndex: 10001 }}
				MenuListProps={{
					'aria-labelledby': 'basic-button',
				}}
			>
				{profileRoutes.map(link => selectMenuItem(link))}
			</Menu>
		</>
	);
};

UserProfileDropDown.propTypes = NavLinksType;

export default UserProfileDropDown;
